import { apiRequest } from '@/core';

export type Amenity = {
  id: number;
  amenity_category_id?: number;
  name: string;
  sort_order?: number;
  target_types?: AmenityTargetType[];
  created_at?: string;
  updated_at?: string;
};

export type AmenityTargetType = 'property' | 'room';

export type AmenityCategory = {
  id: number;
  name: string;
  sort_order?: number;
  amenities: Amenity[];
};

export type PropertyType = {
  id: number;
  name: string;
};

export type Area = {
  id: number;
  name: string;
  latitude?: number | null;
  longitude?: number | null;
  points?: Array<{
    latitude: number | string;
    longitude: number | string;
  }>;
};

export type Season = {
  id: number;
  name: string;
  active: boolean;
  current: boolean;
};

export type Country = {
  id: number;
  name: string;
};

type ListResponse<T> = T[] | { data?: T[] };

const normalizeList = <T>(response: ListResponse<T>) =>
  Array.isArray(response) ? response : (response.data ?? []);

type AmenityListResponse = ListResponse<Amenity | AmenityCategory>;
type AmenityCategoryListResponse = ListResponse<AmenityCategory>;

const isAmenityCategory = (
  item: Amenity | AmenityCategory,
): item is AmenityCategory =>
  Array.isArray((item as AmenityCategory).amenities);

const normalizeAmenities = (response: AmenityListResponse): Amenity[] =>
  normalizeList(response).flatMap((item) =>
    isAmenityCategory(item) ? item.amenities : item,
  );

const filterAmenityCategory = (
  category: AmenityCategory,
  predicate: (amenity: Amenity) => boolean,
) => ({
  ...category,
  amenities: category.amenities.filter(predicate),
});

const hasAmenityTarget = (amenity: Amenity, targetType: AmenityTargetType) =>
  amenity.target_types?.includes(targetType) ?? false;

export const isPropertyAmenity = (amenity: Amenity) =>
  amenity.target_types?.includes('property') ?? true;

export const isRoomAmenity = (amenity: Amenity) =>
  amenity.target_types?.includes('room') ?? true;

export const fetchAmenities = (signal?: AbortSignal) =>
  apiRequest<AmenityListResponse>({
    endpoint: 'v1/amenities',
    signal,
  }).then((response) => ({ data: normalizeAmenities(response) }));

export const fetchAmenityCategories = (
  targetType?: AmenityTargetType,
  signal?: AbortSignal,
) =>
  apiRequest<AmenityCategoryListResponse>({
    endpoint: 'v1/amenities',
    params: targetType ? { 'filter[target_types]': targetType } : undefined,
    signal,
  }).then((response) => {
    const categories = normalizeList(response);

    return {
      data: targetType
        ? categories
            .map((category) =>
              filterAmenityCategory(category, (amenity) =>
                hasAmenityTarget(amenity, targetType),
              ),
            )
            .filter((category) => category.amenities.length > 0)
        : categories,
    };
  });

export const fetchPropertyTypes = (signal?: AbortSignal) =>
  apiRequest<ListResponse<PropertyType>>({
    endpoint: 'v1/property_types',
    signal,
  }).then((response) => ({ data: normalizeList(response) }));

export const fetchAreas = (signal?: AbortSignal) =>
  apiRequest<ListResponse<Area>>({ endpoint: 'v1/areas', signal }).then(
    (response) => ({ data: normalizeList(response) }),
  );

export const fetchSeasons = (signal?: AbortSignal) =>
  apiRequest<ListResponse<Season>>({ endpoint: 'v1/seasons', signal }).then(
    (response) => ({ data: normalizeList(response) }),
  );

export type PropertyStatusEntry = {
  id: number;
  name: string;
  slug?: string;
};

export const fetchCountries = () =>
  apiRequest<ListResponse<Country>>({ endpoint: 'v1/countries' }).then(
    (response) => ({ data: normalizeList(response) }),
  );

export const fetchStatuses = (options?: { locale?: string }) =>
  apiRequest<ListResponse<PropertyStatusEntry>>({
    endpoint: 'v1/statuses',
    locale: options?.locale,
  }).then((response) => ({ data: normalizeList(response) }));

export const findAreaByCoordinates = (lat: number, lng: number) =>
  apiRequest<Area>({
    endpoint: 'v1/areas/find-by-coordinates',
    method: 'GET',
    params: { latitude: lat, longitude: lng },
  });
